import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import Global from '../../../store/global-selectors';
import { disconnectProviderRequest } from '../../../store/freight-settings/freight-settings.actions';
import { useModal } from '../../../hooks/use-modal';
import { ConnectFreightProviderModal } from './components/connect-freight-provider-modal/connect-freight-provider-modal';
import { freightSettingsMessages } from './messages';
import style from './freight-settings-page.module.less';

import { DateTime } from 'luxon';

export const FreightSettingsPage = () => {
  const dispatch = useDispatch();
  const providers = useSelector(Global.selectFreightSettingsProviders);
  const isLoadingProviders = useSelector(Global.selectFreightSettingsIsLoadingProviders);
  const isDisconnecting = useSelector(Global.selectFreightSettingsIsDisconnecting);

  const connectModal = useModal();
  const [selectedProvider, setSelectedProvider] = useState<{ freightProviderId: number; name: string; apiCode: string } | null>(null);

  const handleConnect = useCallback(
    (freightProviderId: number, providerName: string, providerApiCode: string) => {
      setSelectedProvider({ freightProviderId, name: providerName, apiCode: providerApiCode });
      connectModal.open();
    },
    [connectModal],
  );

  const handleDisconnect = useCallback(
    (sellerFreightProviderId: number) => {
      dispatch(disconnectProviderRequest(sellerFreightProviderId));
    },
    [dispatch],
  );

  const handleCloseModal = useCallback(() => {
    connectModal.close();
    setSelectedProvider(null);
  }, [connectModal]);

  const providerCards = useMemo(
    () =>
      providers.map((provider) => {
        const connectedDate = provider.connectedAt ? DateTime.fromISO(provider.connectedAt).toLocaleString(DateTime.DATE_MED) : null;

        return (
          <div key={provider.code} className={style['provider-card']}>
            <FlexRow gap={Gap.Space16}>
              <FlexColumn>
                <Text textType={TextType.H4} value={provider.name} />
                {provider.isConnected ? (
                  <FlexColumn gap={Gap.Space4}>
                    <Text
                      className={style['provider-card__status--connected']}
                      textType={TextType.Paragraph}
                      value={<FormattedMessage {...freightSettingsMessages.connected} />}
                    />
                    {connectedDate ? (
                      <Text
                        textType={TextType.Caption}
                        value={<FormattedMessage {...freightSettingsMessages.connectedAt} values={{ date: connectedDate }} />}
                      />
                    ) : null}
                  </FlexColumn>
                ) : (
                  <Text
                    className={style['provider-card__status--disconnected']}
                    textType={TextType.Paragraph}
                    value={<FormattedMessage {...freightSettingsMessages.notConnected} />}
                  />
                )}
              </FlexColumn>
              <FlexRow>
                {provider.isConnected ? (
                  <Button
                    disabled={isDisconnecting}
                    onClick={() => handleDisconnect(provider.sellerFreightProviderId!)}
                    theme={THEME_CONSTANTS.danger}
                  >
                    <FormattedMessage {...freightSettingsMessages.disconnectButton} />
                  </Button>
                ) : (
                  <Button onClick={() => handleConnect(provider.freightProviderId, provider.name, provider.apiCode)}>
                    <FormattedMessage {...freightSettingsMessages.connectButton} />
                  </Button>
                )}
              </FlexRow>
            </FlexRow>
          </div>
        );
      }),
    [providers, isDisconnecting, handleConnect, handleDisconnect],
  );

  if (isLoadingProviders) {
    return <LoadingIndicator />;
  }

  return (
    <FlexColumn className={style['freight-settings-page']}>
      <div className={style['freight-settings-page__header']}>
        <Text textType={TextType.H2} value={<FormattedMessage {...freightSettingsMessages.pageTitle} />} />
        <Text textType={TextType.Paragraph} value={<FormattedMessage {...freightSettingsMessages.pageDescription} />} />
      </div>
      <FlexColumn className={style['freight-settings-page__providers']} gap={Gap.Space16}>
        {providerCards}
      </FlexColumn>
      {connectModal.isOpen && selectedProvider ? (
        <ConnectFreightProviderModal
          freightProviderId={selectedProvider.freightProviderId}
          onClose={handleCloseModal}
          providerName={selectedProvider.name}
        />
      ) : null}
    </FlexColumn>
  );
};
